import MomentUtils from '@date-io/moment';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Input,
  InputAdornment,
  InputLabelProps,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Canceler } from 'axios';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StoryList from '../../components/StoryList';
import StoryItem from '../../components/StoryItem';
import { SubmitControl } from '../../components/SubmitControl';
import useBackdrop from '../../hooks/useBackdrop';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import { deleteAbtestItem, getAbtestItem, getEpisodeAbTests, saveAbtestItem, saveBundlePackageAbtest } from '../../services/api/Abtest';
import api from '../../services/api/base';
import { getSimpleUserSegments } from '../../services/api/Segment';
import { getStoryById } from '../../services/api/Story';
import settings from '../../services/settings';
import {
  AB_TEST_AUDIENCE_TYPE,
  AB_TEST_TARGET_TYPE,
  AB_TEST_TYPE,
  IABTest,
  IBundleVariant,
  IDisplayVariant,
  IEpisodeVariant,
  IPerformanceCollectionVariant,
  IPerformanceFeaturdCardVariant,
  IPerformanceVariant,
  IStory,
} from '../../services/types';
import { getInitialBundle } from '../bundlePackage/BundlePackageItem';
import StorySelectModal from '../../components/StorySelectModal';
import BundleVariant, { IBundleVariantImperativeHandle } from './BundleVariant';
import DisplayVariant from './DisplayVariant';
import EpisodeVariant from './EpisodeVariant';
import { PerformanceCollectionVariant, PerformanceFeaturedCardVariant } from './PerformanceVariant';
import useStyles from './styles';

interface IABTestViewProps {
  testId?: number;
  onCreated: () => void;
  onDeleted?: () => void;
}
interface ISegmentSelect {
  onChange: (segmentId: any) => void;
  segments: any[] | undefined;
  selectedValue: number | undefined | string | '';
  disabled: boolean;
}

const ABTestView = (props: IABTestViewProps): JSX.Element => {
  const classes = useStyles();
  const canceler = useRef<Canceler>(() => undefined);
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const confirm = useConfirmDialog();
  const createNewExperiment = (): IABTest => {
    return {
      id: null,
      testName: '',
      variants: [],
      startDate: moment()
        .startOf('day')
        .toDate(),
      endDate: moment()
        .endOf('day')
        .toDate(),
      testType: AB_TEST_TYPE.DISPLAY,
      testTargetType: AB_TEST_TARGET_TYPE.STORY,
      testTargetValue: '',
      audienceType: AB_TEST_AUDIENCE_TYPE.SEGMENT,
      audienceValue: 0,
      segmentId: undefined,
    };
  };

  const [experiment, setExperiment] = useState<IABTest>(createNewExperiment());
  const [editable, setEditable] = useState<boolean>(true);
  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);
  const [segments, setSegments] = useState<any[] | undefined>();
  const [episodeAbTestList, setEpisodeAbTestList] = useState<any[]>([]);
  const [selectedStory, setSelectedStory] = useState<IStory>();
  const [performanceType, setPerformanceType] = useState<'story' | 'season'>('story');

  const bundleVariantRefs = useRef<(IBundleVariantImperativeHandle | null)[]>([]);

  const onChangeValue = (key: string, value: any) => {
    const changes = { [key]: value };
    if (key === 'audienceType') {
      changes['audienceValue'] = 0;
    }

    if (key === 'testTargetType' && experiment.testType === AB_TEST_TYPE.DISPLAY && selectedStory) {
      confirm.open({
        title: 'Wait!',
        text: 'You have unsaved changes. Are you sure you want to discard?',
        onProceed: () => {
          setExperiment({
            ...experiment,
            ...changes,
            testTargetValue: '',
            variants: [],
            segmentId: undefined,
          });
          setSelectedStory(undefined);
        },
      });
    } else {
      setExperiment({ ...experiment, ...changes });
    }
  };

  const onChangeTestType = useCallback(
    (e, testType) => {
      if (testType === AB_TEST_TYPE.PERFORMANCE) {
        snackbar.error('Cannot select this type of test.');
      } else if (testType === AB_TEST_TYPE.DISPLAY) {
        setExperiment({
          ...experiment,
          testType,
          testTargetType: AB_TEST_TARGET_TYPE.STORY,
          testTargetValue: '',
          audienceType: AB_TEST_AUDIENCE_TYPE.SEGMENT,
          audienceValue: 0,
          segmentId: undefined,
          variants: [],
        });
      } else if (testType === AB_TEST_TYPE.EPISODE) {
        setExperiment({
          ...experiment,
          testType,
          testTargetType: AB_TEST_TARGET_TYPE.EPISODE,
          testTargetValue: '',
          audienceType: AB_TEST_AUDIENCE_TYPE.SEGMENT,
          audienceValue: 0,
          segmentId: undefined,
          variants: [],
        });
      } else if (testType === AB_TEST_TYPE.BUNDLE) {
        setExperiment({
          ...experiment,
          testType,
          testTargetType: AB_TEST_TARGET_TYPE.STORY,
          testTargetValue: '',
          audienceType: AB_TEST_AUDIENCE_TYPE.RANDOM,
          audienceValue: 100,
          segmentId: undefined,
          variants: [],
        });
      }
      setSelectedStory(undefined);
    },
    [experiment, snackbar],
  );

  const addVariant = () => {
    switch (experiment.testType) {
      case AB_TEST_TYPE.DISPLAY:
        addDisplayVariant();
        break;
      case AB_TEST_TYPE.PERFORMANCE:
        addStoryVariant();
        break;
      case AB_TEST_TYPE.EPISODE:
        addEpisodeVariant();
        break;
      case AB_TEST_TYPE.BUNDLE:
        addBundleVariant();
        break;
      default:
        snackbar.error('Unhandled test type');
    }
  };

  const addDisplayVariant = (): void => {
    const newVariant: IDisplayVariant = {
      data: {
        id: undefined,
        title: '',
        summary: '',
        tagLine: '',
        additionalCoverVUrl: '',
        additionalCoverHUrl: '',
      },
    };

    setExperiment(ps => ({
      ...ps,
      variants: [...ps.variants, newVariant],
    }));
  };

  const addStoryVariant = (): void => {
    const newVariant: IPerformanceVariant =
      experiment.testTargetType === AB_TEST_TARGET_TYPE.FEATUREDCARDS
        ? {
            data: {
              type: performanceType,
              objectId: 0,
              subTitle: '',
              actionText: '',
            },
          }
        : {
            data: {
              type: performanceType,
              storyId: 0,
              seasonId: performanceType === 'season' ? 0 : undefined,
              storyGenreText: '',
            },
          };

    setExperiment(ps => ({
      ...ps,
      variants: [...ps.variants, newVariant],
    }));
  };

  const addEpisodeVariant = (): void => {
    const newVariant: IEpisodeVariant = {
      id: undefined,
      data: {},
    };

    setExperiment(ps => ({
      ...ps,
      variants: [...ps.variants, newVariant],
    }));
  };

  const addBundleVariant = (): void => {
    const newVariant: IBundleVariant = {
      id: undefined,
      data: {
        type: 'episode_bundle_price',
        bundles: [
          getInitialBundle({
            storyId: selectedStory?.id,
            targetType: 'ab_test',
          }),
        ],
      },
    };

    setExperiment(ps => ({
      ...ps,
      variants: [...ps.variants, newVariant],
    }));
  };

  const onChangeVariant = (i: number, k: string, v: any) => {
    experiment.variants[i] = { ...experiment.variants[i], [k]: v }; // mutation... 일단 패스함.

    setExperiment({
      ...experiment,
    });
  };
  const onChangeVariantProp = (i: number, k: string, v: any) => {
    const newExperiment = { ...experiment };
    newExperiment.variants[i].data[k] = v;
    setExperiment(newExperiment);
  };
  const onChangeVariantProps = (i: number, prop: Partial<IPerformanceVariant | IDisplayVariant | IEpisodeVariant | IBundleVariant>) => {
    const newExperiment = { ...experiment };
    newExperiment.variants[i].data = { ...newExperiment.variants[i].data, ...prop };
    setExperiment(newExperiment);
  };
  const removeVariant = (i: number) => {
    const newExperiment = { ...experiment };
    newExperiment.variants.splice(i, 1);
    setExperiment(newExperiment);
  };

  const fetchTest = useCallback(async () => {
    if (!props.testId) {
      return;
    }

    try {
      const result = await getAbtestItem(props.testId);
      const experiment = result.data.item;
      if (
        experiment.testType === AB_TEST_TYPE.PERFORMANCE &&
        experiment.variants.length > 0 &&
        experiment.variants[0].data &&
        experiment.variants[0].data.seasonId
      ) {
        setPerformanceType('season');
      }

      if (experiment.testType === 'EPISODE') {
        fetchEpisodeAbTests(experiment.testTargetValue);
      }

      if (experiment.testType === AB_TEST_TYPE.BUNDLE) {
        const story = await getStoryById(experiment.testTargetValue);
        setSelectedStory(story.data);

        experiment.variants.forEach((variant: IBundleVariant) => {
          variant.data.bundles = variant.object;
        });
      }

      setExperiment(experiment);
    } catch (e) {
      snackbar.error('Could not get test');
      console.log(e);
    }
  }, [props.testId, snackbar]);

  const fetchSegments = useCallback(async () => {
    if (segments === undefined) {
      canceler.current();

      try {
        const [fetchPromise, cancel] = getSimpleUserSegments();
        canceler.current = cancel;

        const result = await fetchPromise;
        if (result && result.status === 200 && result.data) {
          const { items } = result.data;
          setSegments(items);
        }
      } catch (e) {
        if (api.isCancel(e)) return;
      }
    }
  }, [segments]);

  const fetchEpisodeAbTests = async (storyId: number) => {
    canceler.current();
    try {
      const [fetchPromise, cancel] = getEpisodeAbTests(storyId);
      canceler.current = cancel;

      const result = await fetchPromise;
      setEpisodeAbTestList(result.data);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const onSelectStory = (story: IStory) => {
    setSelectedStory(story);

    if (experiment.testType === 'DISPLAY') {
      setExperiment({
        ...experiment,
        testTargetValue: experiment.testTargetType === AB_TEST_TARGET_TYPE.SEASON ? story.seasonId!.toString() : story.id.toString(),
      });
    } else if (experiment.testType === 'EPISODE') {
      setExperiment({
        ...experiment,
        testTargetValue: story.seasonId!.toString(),
      });
      fetchEpisodeAbTests(story.seasonId!);
    } else if (experiment.testType === 'BUNDLE') {
      if (!story.id) {
        return;
      }

      setExperiment(ps => {
        return {
          ...ps,
          testTargetValue: story.id.toString(),
        };
      });
    }
  };

  const handleClickSubmit = () => {
    const experimentData: IABTest = { ...experiment };
    const { testName, testType, testTargetValue, startDate, endDate, audienceType, audienceValue } = experiment;
    if (testName.trim() === '' || !startDate || !endDate) {
      snackbar.error('All fields are required.');
      return false;
    }
    if (startDate > endDate) {
      snackbar.error('End date must be the future than start date.');
      return false;
    }

    if (audienceType === 'RANDOM' && audienceValue === 0) {
      snackbar.error('An audience value cannot be 0 if the type is random.');
      return false;
    }

    if (
      !experimentData.segmentId &&
      !((testType === AB_TEST_TYPE.BUNDLE || testType === AB_TEST_TYPE.PERFORMANCE) && audienceType === AB_TEST_AUDIENCE_TYPE.RANDOM)
    ) {
      snackbar.error('Select Segment.');
      return false;
    }

    if (experimentData.testType === AB_TEST_TYPE.PERFORMANCE) {
      if (experimentData.variants.length < 2) {
        snackbar.error('You need to add at least 2 variants.');
        return false;
      }

      if (experimentData.testTargetType === AB_TEST_TARGET_TYPE.FEATUREDCARDS) {
        if (experimentData.variants.filter(v => v.data.objectId && v.data.subTitle && v.data.actionText).length !== experimentData.variants.length) {
          snackbar.error('All fields are required.');
          return false;
        }
      } else {
        if (
          experimentData.variants.filter(v => v.data.storyId && (v.data.seasonId || v.data.type === 'story')).length !==
          experimentData.variants.length
        ) {
          snackbar.error('All fields are required.');
          return false;
        }
      }
    }

    if (testType === AB_TEST_TYPE.BUNDLE) {
      const isValid =
        selectedStory?.id &&
        bundleVariantRefs.current &&
        bundleVariantRefs.current.reduce((acc, cur) => acc && !!cur && cur.getValidationMessage().length === 0, true);

      if (!isValid) {
        snackbar.error('Bundle package setting is wrong. Please check the input value.');
        return false;
      }
    }

    if (experimentData.variants.length === 0) {
      snackbar.error('No variant added.');
      return false;
    }

    experimentData.startDate = moment.tz(experimentData.startDate, 'EST').toDate();
    experimentData.endDate = moment
      .tz(experimentData.endDate, 'EST')
      .endOf('day')
      .startOf('second')
      .toDate();

    experimentData.audienceValue = Number(experimentData.audienceValue);

    backdrop.show();

    const save = (abTest: IABTest) => {
      if (testType === AB_TEST_TYPE.BUNDLE) {
        (abTest.variants as IBundleVariant[]).forEach((variant: IBundleVariant) => {
          variant.bundles = variant.data.bundles.map(bundle => {
            const { prices, showRemainAll, minRemainEpisodesNumber, maxRemainEpisodesNumber } = bundle;
            return {
              prices: prices.filter(price => price.episodeNum !== 0),
              showRemainAll,
              minRemainEpisodesNumber,
              maxRemainEpisodesNumber,
            };
          });
        });

        return saveBundlePackageAbtest(Number(testTargetValue!), {
          targetType: 'ab_test',
          abTest,
        });
      } else {
        return saveAbtestItem(abTest);
      }
    };

    const handleErrorCode = (errorCode?: string) => {
      if (errorCode === 'DUPLICATED_SEASON_OR_SLOT') {
        snackbar.error(experimentData.testType === AB_TEST_TYPE.PERFORMANCE ? 'The slot is occupied.' : 'The season is already in use.');
      } else if (errorCode === 'DUPLICATE_TEST_FOR_SAME_STORY') {
        snackbar.error('The story is already in use.');
      } else {
        snackbar.error('Failed to save.');
      }
    };

    save(experimentData)
      .then(({ data }) => {
        if (!data.success && data.errorCode) {
          handleErrorCode(data.errorCode);
          return;
        }

        snackbar.success('Test saved.');
        props.onCreated();
        setExperiment(createNewExperiment());
        setSelectedStory(undefined);
      })
      .catch(e => {
        handleErrorCode(e.response.data.errorCode);
      })
      .finally(() => {
        backdrop.hide();
      });
  };

  const handleClickDelete = () => {
    confirm.open({
      title: 'Are you sure to delete this test?',
      text: 'This will affect the actual database.',
      onProceed: () => {
        deleteThisTest();
      },
    });
  };

  const deleteThisTest = async () => {
    try {
      await deleteAbtestItem(props.testId!);
      props.onDeleted!();
    } catch (e) {
      snackbar.error('Could not delete test');
      console.log(e);
    }
  };

  const getStoryModalFilter = (): 'story' | 'season' => {
    if (experiment.testType === AB_TEST_TYPE.BUNDLE || (experiment.testType === AB_TEST_TYPE.PERFORMANCE && performanceType === 'story')) {
      return 'story';
    } else if (experiment.testType === AB_TEST_TYPE.DISPLAY) {
      return experiment.testTargetType === AB_TEST_TARGET_TYPE.STORY ? 'story' : 'season';
    } else {
      return 'season';
    }
  };

  const getTestTargetTypeName = () => (experiment.testTargetType === AB_TEST_TARGET_TYPE.STORY ? 'Story' : 'Season');

  useEffect(() => {
    if (experiment.id !== 0 && experiment.id !== null) {
      setEditable(false);
    }
  }, [experiment]);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  const inputLabelProps: InputLabelProps = { shrink: true, classes: { root: classes.inputLabel } };

  if (props.testId && !experiment.id) {
    return <Paper />;
  }

  return (
    <div>
      <Box display="flex" flexDirection="row" alignItems="center" paddingBottom={2}>
        <Typography variant="h6" component="h3" style={{ flex: 1 }} color="primary">
          {!experiment.id ? '' : <div>{experiment.testName}</div>}
        </Typography>
        {!experiment.id && (
          <SubmitControl
            mode={'create'}
            onSubmitClick={() => {
              handleClickSubmit();
            }}
          />
        )}
        {experiment.id && experiment.isUsable && (
          <Button color="secondary" variant="contained" onClick={handleClickDelete}>
            Delete
          </Button>
        )}
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid id="TestSettingWrap" item xs={6}>
          <Box marginBottom={2}>
            <Typography variant="h6" component="h3">
              Test Setting
            </Typography>
          </Box>
          <Paper className={classes.paperLeft}>
            <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Test Name"
                  required
                  fullWidth
                  disabled={experiment.id != null}
                  value={experiment.testName || ''}
                  onChange={e => onChangeValue('testName', e.target.value)}
                  InputLabelProps={inputLabelProps}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl>
                      <FormLabel className={classes.inputLabel} required>
                        Start
                      </FormLabel>
                      <MuiPickersUtilsProvider utils={MomentUtils}>
                        <DatePicker
                          format="YYYY-MM-DD"
                          required
                          margin="normal"
                          disabled={!editable}
                          value={experiment.startDate}
                          disablePast
                          minDateMessage=""
                          onChange={data => {
                            if (data) {
                              onChangeValue('startDate', data.startOf('day').toDate());
                            }
                          }}
                        />
                      </MuiPickersUtilsProvider>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl>
                      <FormLabel className={classes.inputLabel} required>
                        End
                      </FormLabel>
                      <MuiPickersUtilsProvider utils={MomentUtils}>
                        <DatePicker
                          format="YYYY-MM-DD"
                          required
                          margin="normal"
                          id="date-picker-inline"
                          disabled={!editable}
                          value={experiment.endDate}
                          disablePast
                          minDateMessage=""
                          onChange={data => {
                            if (data) {
                              onChangeValue('endDate', data.endOf('day').toDate());
                            }
                          }}
                        />
                      </MuiPickersUtilsProvider>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormControl>
                  <FormLabel className={classes.inputLabel} required>
                    Test Type
                  </FormLabel>
                  <RadioGroup row value={experiment.testType} name="testType" onChange={onChangeTestType}>
                    {experiment.id !== null && (
                      <FormControlLabel control={<Radio value="PERFORMANCE" color="primary" disabled={true} />} label="Performance" />
                    )}
                    <FormControlLabel control={<Radio value="DISPLAY" color="primary" disabled={experiment.id != null} />} label="Display" />
                    <FormControlLabel control={<Radio value="EPISODE" color="primary" disabled={experiment.id != null} />} label="Episode" />
                    <FormControlLabel control={<Radio value="BUNDLE" color="primary" disabled={experiment.id != null} />} label="Bundle Package" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid id="TypeSettingWrap" item xs={6}>
          <Box marginBottom={2}>
            <Typography variant="h6" component="h3">
              Type Setting
            </Typography>
          </Box>
          <Paper className={classes.paperLeft}>
            {experiment && experiment.testType === AB_TEST_TYPE.PERFORMANCE && (
              <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={3}>
                {editable && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel className={classes.inputLabel} required>
                        Performance Type
                      </FormLabel>
                      <RadioGroup
                        row
                        value={performanceType}
                        name="performanceType"
                        onChange={(e, v) => {
                          setPerformanceType(e.target.value === 'season' ? 'season' : 'story');
                        }}
                      >
                        <FormControlLabel
                          control={<Radio color="primary" value="story" disabled={!editable || experiment.variants.length > 0} />}
                          label="STORY"
                        />
                        <FormControlLabel
                          control={<Radio color="primary" value="season" disabled={!editable || experiment.variants.length > 0} />}
                          label="SEASON"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {experiment.audienceType === AB_TEST_AUDIENCE_TYPE.SEGMENT_NO_CONTROL && (
                    <SegmentSelect
                      segments={segments}
                      selectedValue={experiment.segmentId}
                      disabled={!editable}
                      onChange={selectedId => {
                        onChangeValue('segmentId', selectedId);
                      }}
                    />
                  )}
                  {experiment.audienceType === 'RANDOM' && (
                    <TextField
                      placeholder=""
                      value={experiment.audienceValue}
                      name={'audienceValue'}
                      disabled={!editable}
                      onChange={e => {
                        onChangeValue('audienceValue', e.target.value);
                      }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel className={classes.inputLabel} required>
                      Slot
                    </FormLabel>
                    <RadioGroup
                      row
                      value={experiment.testTargetType}
                      name="testTargetType"
                      onChange={e => {
                        onChangeValue('testTargetType', e.target.value);
                      }}
                    >
                      <FormControlLabel
                        control={<Radio color="primary" value="FEATURED_CARDS" disabled={!editable || experiment.variants.length > 0} />}
                        label="Featured Card"
                      />
                      <FormControlLabel
                        control={<Radio color="primary" value="COLLECTION" disabled={!editable || experiment.variants.length > 0} />}
                        label="Collection"
                      />
                    </RadioGroup>
                  </FormControl>
                  {experiment.testTargetType !== 'COLLECTION' && (
                    <Select
                      value={experiment.testTargetValue ? parseInt(experiment.testTargetValue) : 0}
                      onChange={(e, c) => {
                        onChangeValue('testTargetValue', Number(e.target.value));
                      }}
                      disabled={!editable}
                    >
                      {Array.from({ length: 9 }, (v, i) => (
                        <MenuItem key={`slot-${i}`} value={i}>
                          {i}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  {experiment.testTargetType == 'COLLECTION' && (
                    <TextField
                      placeholder="Collection Tracking ID"
                      disabled={!editable}
                      value={experiment.testTargetValue || ''}
                      onChange={e => onChangeValue('testTargetValue', e.target.value)}
                    />
                  )}
                </Grid>
              </Grid>
            )}
            {experiment && experiment.testType === 'DISPLAY' && (
              <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel className={classes.inputLabel}>Target Type</FormLabel>
                    <RadioGroup
                      value={experiment.testTargetType}
                      style={{ flexDirection: 'row', marginBottom: 16 }}
                      onChange={e => {
                        onChangeValue('testTargetType', e.target.value);
                      }}
                    >
                      <FormControlLabel value={AB_TEST_TARGET_TYPE.STORY} control={<Radio />} label="Story" disabled={!editable} />
                      <FormControlLabel value={AB_TEST_TARGET_TYPE.SEASON} control={<Radio />} label="Season" disabled={!editable} />
                    </RadioGroup>
                    <FormLabel className={classes.inputLabel} required>
                      {getTestTargetTypeName()}
                    </FormLabel>
                    <Box display="flex">
                      <Input
                        placeholder={`${getTestTargetTypeName()} ID`}
                        value={experiment.testTargetValue}
                        disabled={!editable}
                        style={{ flexGrow: 1 }}
                        onChange={e => {
                          onChangeValue('testTargetValue', e.target.value);
                        }}
                        onClick={editable ? () => setIsStoryModalOpen(true) : undefined}
                        readOnly
                      />
                      <Button variant="contained" color="primary" size="small" onClick={() => setIsStoryModalOpen(true)} disabled={!editable}>
                        {`Select ${getTestTargetTypeName()}`}
                      </Button>
                    </Box>
                    {selectedStory && (
                      <Box>
                        <StoryList readonly stories={[selectedStory]} hideSearch />
                      </Box>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <SegmentSelect
                    segments={segments}
                    selectedValue={experiment.segmentId}
                    disabled={!editable}
                    onChange={selectedId => {
                      onChangeValue('segmentId', selectedId);
                    }}
                  />
                </Grid>
              </Grid>
            )}
            {experiment && experiment.testType === 'EPISODE' && (
              <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel className={classes.inputLabel} required>
                      Select Segment
                    </FormLabel>
                    <SegmentSelect
                      segments={segments}
                      selectedValue={experiment.segmentId}
                      disabled={!editable}
                      onChange={selectedId => {
                        onChangeValue('segmentId', selectedId);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel className={classes.inputLabel} required>
                      Season
                    </FormLabel>
                    <Box display="flex">
                      <Input
                        placeholder="Season ID"
                        readOnly
                        value={experiment.testTargetValue}
                        disabled={!editable}
                        style={{ flexGrow: 1, marginRight: 10 }}
                        onChange={e => {
                          onChangeValue('testTargetValue', e.target.value);
                        }}
                        onClick={() => setIsStoryModalOpen(true)}
                      />
                      <Button variant="contained" color="primary" size="small" onClick={() => setIsStoryModalOpen(true)} disabled={!editable}>
                        Select Season
                      </Button>
                    </Box>
                    {selectedStory && (
                      <Box>
                        <StoryList readonly stories={[selectedStory]} hideSearch />
                      </Box>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            )}
            {experiment && experiment.testType === 'BUNDLE' && (
              <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel className={classes.inputLabel} required>
                      Story
                    </FormLabel>
                    <Box display="flex">
                      <Input
                        placeholder="Story ID"
                        readOnly
                        value={experiment.testTargetValue}
                        disabled={!editable}
                        style={{ flexGrow: 1, marginRight: 10 }}
                        onChange={e => {
                          onChangeValue('testTargetValue', e.target.value);
                        }}
                        onClick={() => setIsStoryModalOpen(true)}
                      />
                      <Button variant="contained" color="primary" size="small" onClick={() => setIsStoryModalOpen(true)} disabled={!editable}>
                        Select Story
                      </Button>
                    </Box>
                    {selectedStory && (
                      <Box>
                        <StoryItem
                          readonly
                          story={selectedStory}
                          extraText={`Episode count = ${selectedStory.episodeCount} | Episode price = ${selectedStory.episodePrice || 3}`}
                          onItemClick={() => window.open(`${settings.getCrewURI()}/stories/${selectedStory.id}`)}
                        />
                      </Box>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <FormLabel className={classes.inputLabel} required>
                      Exposed (Audience Value)
                    </FormLabel>
                    <TextField
                      placeholder=""
                      value={experiment.audienceValue}
                      name={'audienceValue'}
                      disabled={!editable}
                      onChange={e => {
                        onChangeValue('audienceValue', e.target.value);
                      }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
        <Grid id="VariantsWrap" item xs={12}>
          <Box display="flex" flexDirection="row" alignItems="center" paddingBottom={2}>
            <Typography variant="h6" component="h3" style={{ flex: 1 }}>
              Variants
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={addVariant}
              disabled={
                !editable ||
                ((experiment.testType === 'DISPLAY' || experiment.testType === 'EPISODE' || experiment.testType === 'BUNDLE') &&
                  !experiment.testTargetValue)
              }
            >
              Add Variant
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box>
            {experiment.testType === AB_TEST_TYPE.PERFORMANCE &&
              experiment.variants.map((variant: IPerformanceFeaturdCardVariant | IPerformanceCollectionVariant, i: number) =>
                experiment.testTargetType === AB_TEST_TARGET_TYPE.FEATUREDCARDS ? (
                  <PerformanceFeaturedCardVariant
                    performanceType={performanceType!}
                    testTargetType={experiment.testTargetType!}
                    key={`story${i}`}
                    variant={variant as IPerformanceFeaturdCardVariant} // subTitle, action text (carousel) or story genre text (collection)
                    onChangeVariantProp={(k, v) => onChangeVariantProp(i, k, v)}
                    onChangeVariantProps={kv => onChangeVariantProps(i, kv)}
                    removeVariant={() => removeVariant(i)}
                    editable={editable}
                  />
                ) : (
                  <PerformanceCollectionVariant
                    performanceType={performanceType!}
                    testTargetType={experiment.testTargetType!}
                    key={`story${i}`}
                    variant={variant as IPerformanceCollectionVariant} // subTitle, action text (carousel) or story genre text (collection)
                    onChangeVariantProp={(k, v) => onChangeVariantProp(i, k, v)}
                    onChangeVariantProps={kv => onChangeVariantProps(i, kv)}
                    removeVariant={() => removeVariant(i)}
                    editable={editable}
                  />
                ),
              )}
            {experiment.testType === 'DISPLAY' &&
              experiment.variants.map((variant, i: number) => (
                <DisplayVariant
                  key={`dv${i}`}
                  testTargetType={experiment.testTargetType!}
                  variant={variant as IDisplayVariant}
                  onChangeVariantProp={(k, v) => onChangeVariantProp(i, k, v)}
                  removeVariant={() => removeVariant(i)}
                  editable={editable}
                />
              ))}
            {experiment.testType === 'EPISODE' &&
              experiment.variants.map((variant, i: number) => (
                <EpisodeVariant
                  key={`ev${i}`}
                  variant={variant as IEpisodeVariant}
                  episodeAbTestList={episodeAbTestList}
                  removeVariant={() => removeVariant(i)}
                  onChangeVariant={(k, v) => onChangeVariant(i, k, v)}
                  editable={editable}
                />
              ))}
            {experiment.testType === AB_TEST_TYPE.BUNDLE &&
              selectedStory &&
              experiment.variants.map((variant, i: number) => (
                <BundleVariant
                  ref={el => {
                    bundleVariantRefs.current[i] = el;
                  }}
                  key={`bv${i}`}
                  no={i + 1}
                  story={selectedStory}
                  variant={variant as IBundleVariant}
                  removeVariant={() => removeVariant(i)}
                  onChangeVariantProps={kv => onChangeVariantProps(i, kv)}
                  editable={editable}
                />
              ))}
          </Box>
        </Grid>
      </Grid>
      <StorySelectModal
        title={getStoryModalFilter() === 'story' ? 'Select Story' : 'Select Season'}
        open={isStoryModalOpen}
        onClick={(story: IStory) => {
          setIsStoryModalOpen(false);
          onSelectStory(story);
        }}
        onClose={() => setIsStoryModalOpen(false)}
        filter={getStoryModalFilter() === 'story' ? 'story' : 'season'}
      />
    </div>
  );
};

const SegmentSelect = (props: ISegmentSelect): JSX.Element => {
  const { segments, selectedValue, disabled, onChange } = props;
  if (segments === undefined) {
    return <div>Loading segments...</div>;
  }

  return (
    <Select
      autoWidth={true}
      value={selectedValue || 0}
      onChange={(e, c) => {
        onChange(e.target.value);
      }}
      disabled={disabled}
    >
      <MenuItem key={'segment-00'} value={0}>
        Select segment
      </MenuItem>
      {segments!.map(segment => (
        <MenuItem key={`segment-${segment.id}`} value={segment.id}>{`[${segment.type}] ${segment.name}`}</MenuItem>
      ))}
    </Select>
  );
};

export default ABTestView;
